import React from "react";
import * as Mui from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { designColors, StatCard } from "services/constants/designTokens";

// Same accent/tint mapping as the other split screens' status cards so
// "Pending"/"Approved"/"Rejected" read the same color everywhere in Team
// Zone. Advance Salary's post-approval lifecycle is richer than other
// screens (Approved/Active/Partially-Paid/Pre-Closed/Closed) — all of
// those bucket into the "Approved" card here, the same way the existing
// buildStatusHistory timeline helper already groups them together. This
// bucketing is ONLY for the stat cards/accent-color lookup — the list
// table and detail panel status chip keep showing the real raw status
// text (e.g. "Partially-Paid"), since that's meaningful loan-progress
// information, not just an approval decision.
const STATUS_CARD_DEFS = [
  {
    key: "Total Requests",
    label: "Total Requests",
    detail: "This Year",
    icon: ListAltIcon,
    accent: "#7C3AED",
    tint: "#F3E8FF",
  },
  {
    key: "Pending",
    label: "Pending",
    detail: "Approval Pending",
    icon: AccessTimeIcon,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    label: "Approved",
    detail: "Total Approved",
    icon: CheckCircleOutlineIcon,
    accent: "#16A34A",
    tint: "#EAFBF1",
  },
  {
    key: "Paid",
    label: "Paid",
    detail: "Disbursed",
    icon: AccountBalanceOutlinedIcon,
    accent: "#2563EB",
    tint: "#EFF6FF",
  },
  {
    key: "Rejected",
    label: "Rejected",
    detail: "Not Approved",
    icon: HighlightOffIcon,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    label: "Cancelled",
    detail: "Cancelled",
    icon: HighlightOffIcon,
    accent: "#64748B",
    tint: "#F1F5F9",
  },
];

// Bucket the API's `status` string into one of the three canonical
// statuses above (case/spelling tolerant). Approved/Active/Partially-Paid/
// Pre-Closed/Closed all count as "Approved" here.
export const canonicalAdvanceSalaryStatus = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "pending") return "Pending";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  if (
    key === "approved" ||
    key === "active" ||
    key === "partially-paid" ||
    key === "pre-closed" ||
    key === "closed"
  ) {
    return "Approved";
  }
  return status;
};

// Single source of truth for a status's accent/tint pair, shared by these
// cards and DetailPanel's header chip (the chip label itself stays raw —
// only its color comes from this canonical bucket).
export const advanceSalaryStatusVisual = (status) => {
  const canonical = canonicalAdvanceSalaryStatus(status);
  return (
    STATUS_CARD_DEFS.find((card) => card.key === canonical) || {
      key: canonical,
      accent: designColors.slate600,
      tint: designColors.slate100,
    }
  );
};

export const AdvanceSalaryStatusCards = ({
  counts,
  activeFilter,
  onCardClick,
  totalRequestsDetail = "This Year",
}) => {
  return (
    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        "@media (max-width:599.95px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {STATUS_CARD_DEFS.map((card) => {
        const isTotalCard = card.key === "Total Requests";
        const detail = isTotalCard ? totalRequestsDetail : card.detail;
        return (
          <StatCard
            activeBorder
            key={card.key}
            icon={card.icon}
            title={card.label}
            value={counts?.[card.key] || 0}
            detail={detail}
            accent={card.accent}
            tint={card.tint}
            isActive={isTotalCard ? !activeFilter : activeFilter === card.key}
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          />
        );
      })}
    </Mui.Box>
  );
};
